import { toast } from "react-toastify";
import { notifyInfo } from "../components/Toast";

export const generateToast = (type, payload = {}) => {
  const toastConfig = payload.toastId
    ? { toastId: payload.toastId }
    : undefined;

  switch (type) {
    case "NO_WALLET":
      notifyInfo(
        "Wallet not detected. Please unlock your wallet extension or open your mobile app.",
      );
      break;

    case "SESSION_EXPIRED":
      notifyInfo(
        "Your session has timed out. Please reconnect wallet to continue your purchase.",
      );
      break;

    case "LOW_GAS":
      notifyInfo(
        "Insufficient funds. You need additional funds to cover the price and network fees.",
        "LOW_GAS",
      );
      break;

    case "USER_REJECTED":
      notifyInfo(
        "Transaction cancelled. To buy this item, please approve the request in your wallet.",
      );
      break;

    case "WRONG_NETWORK":
      notifyInfo(
        "Wrong network. Please switch to the requiredNetwork to finish your purchase.",
        "WRONG_NETWORK",
      );
      break;

    case "REVERT":
      notifyInfo(
        "Transaction failed. This may be due to high network demand or a price change. Try again.",
      );
      break;

    case "REVERT_WITH_REASON":
      notifyInfo(
        "Unable to complete transaction, Please check requirements and try again.",
      );
      break;

    case "GAS_ESTIMATION_FAILED":
      notifyInfo(
        "We couldn’t calculate the network fee. Please try again in a moment.",
      );
      break;

    case "RPC_FAIL":
      notifyInfo(
        "Blockchain connection lost. Please check your internet and reconnect your wallet.",
        "RPC_FAIL",
      );
      break;

    default:
      notifyInfo(payload.message || "Something went wrong. Please try again.");
  }
};

const useWeb3ErrorToast = () => {
  const handleError = (error) => {
    if (!error) return;

    const message =
      typeof error === "string"
        ? error
        : error?.message || error?.error?.message || "";

    const code = error?.code || error?.error?.code;
    const data = error?.data || error?.error?.data;

    /* -------------------------------------------------------
     * Wallet not detected / provider missing
     * ----------------------------------------------------- */
    if (
      /no\s+ethereum|no\s+provider|wallet not found|metamask is not|metamask not installed|unable to detect/i.test(
        message,
      ) ||
      code === "NO_PROVIDER"
    ) {
      generateToast("NO_WALLET");
      return;
    }

    /* -------------------------------------------------------
     * Session expired / disconnected
     * ----------------------------------------------------- */
    if (
      /session expired|already disconnected|provider disconnected/i.test(
        message,
      ) ||
      code === "SESSION_EXPIRED"
    ) {
      generateToast("SESSION_EXPIRED");
      return;
    }

    /* -------------------------------------------------------
     * User rejected transaction
     * ----------------------------------------------------- */
    if (
      /user rejected|user denied|rejected the request/i.test(message) ||
      code === 4001 || // MetaMask
      code === "ACTION_REJECTED"
    ) {
      generateToast("USER_REJECTED");
      return;
    }

    /* -------------------------------------------------------
     * Wrong network / chain mismatch
     * ----------------------------------------------------- */
    if (
      /wrong network|unsupported chain|chain mismatch|incorrect network/i.test(
        message,
      )
    ) {
      generateToast("WRONG_NETWORK");
      return;
    }

    /* -------------------------------------------------------
     * Insufficient funds / low gas
     * ----------------------------------------------------- */
    if (
      /insufficient funds|insufficient balance|not enough funds|insufficient funds for gas|-32000/i.test(
        message,
      ) ||
      code === -32000 ||
      /INSUFFICIENT_FUNDS/i.test(message)
    ) {
      generateToast("LOW_GAS");
      return;
    }

    /* -------------------------------------------------------
     * Gas estimation failed
     * ----------------------------------------------------- */
    if (
      /cannot estimate gas|gas required exceeds allowance|always failing transaction/i.test(
        message,
      )
    ) {
      generateToast("GAS_ESTIMATION_FAILED");
      return;
    }

    /* -------------------------------------------------------
     * Transaction reverted (with or without reason)
     * ----------------------------------------------------- */
    if (
      /execution reverted|revert(ed)?/i.test(message) ||
      (data && /revert/i.test(JSON.stringify(data)))
    ) {
      let reason = "";

      const match = message.match(/revert(?:ed)?:?\s*(.*)$/i);
      if (match && match[1]) {
        reason = match[1];
      } else if (data) {
        try {
          const raw = typeof data === "string" ? data : JSON.stringify(data);
          const match2 = raw.match(/revert(?:ed)?:?\s*([^\\"]+)/i);
          if (match2 && match2[1]) reason = match2[1];
        } catch (_) {}
      }

      if (reason) {
        generateToast("REVERT_WITH_REASON", { reason });
      } else {
        generateToast("REVERT");
      }
      return;
    }

    /* -------------------------------------------------------
     * RPC / Network failures
     * ----------------------------------------------------- */
    if (
      /network error|failed to fetch|connection refused|disconnected|socket hang up|could not connect|timeout|request failed|load failed/i.test(
        message,
      ) ||
      error?.name === "NetworkError" ||
      error?.name === "TimeoutError" ||
      code === "NETWORK_ERROR" ||
      code === -32603 ||
      code === -32005
    ) {
      generateToast("RPC_FAIL");
      return;
    }

    /* -------------------------------------------------------
     * Fallback
     * ----------------------------------------------------- */
    generateToast("DEFAULT", {
      message: message || "Unexpected error occurred.",
    });
  };

  return { handleError };
};

export default useWeb3ErrorToast;
